// The agency DISPLAY-CURRENCY control, shared by both reporting dashboards.
//
// ⚠️ "Native" IS THE DEFAULT AND IS NOT A CURRENCY. It is the per-currency
// behaviour the dashboards have always had — "A$60,176 · NZ$28,927", never
// summed. Picking a real currency converts everything through the monthly rate
// snapshot and blends it into one figure, which is a different (and
// agency-internal) claim, so the control says so and the page carries a note.
//
// The admin extras live in this popover rather than on a settings page because
// this is where you are standing when a rate looks wrong: refresh now, or
// hand-edit the number.

import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useClickOutside } from '../../lib/useClickOutside';
import { cn } from '../../lib/utils';
import {
  NATIVE_DISPLAY_CURRENCY,
  conversionNote,
  displayCurrencyCodes,
  isNativeDisplay,
} from '../../lib/reporting/currencyConversion';
import type { FxRateSnapshot } from '../../types/fxRates';
import { refreshFxRates, saveFxRates } from './api';

export function ConversionNote({
  display,
  snapshot,
  className,
}: {
  display: string;
  snapshot: FxRateSnapshot | null | undefined;
  className?: string;
}): React.JSX.Element | null {
  const note = conversionNote(display, snapshot);
  if (!note) return null;
  return (
    <p className={cn('text-xs text-muted-foreground', className)}>
      {note}
      {/* An agency-internal blend must never be mistaken for a client figure. */}
      <span className="ml-1">· agency view, not a client-facing figure</span>
    </p>
  );
}

export function DisplayCurrencySelect({
  value,
  onChange,
  snapshot,
  isAdmin,
}: {
  value: string;
  onChange: (next: string) => void;
  snapshot: FxRateSnapshot | null | undefined;
  isAdmin: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);
  const qc = useQueryClient();

  const codes = useMemo(() => displayCurrencyCodes(snapshot), [snapshot]);
  const native = isNativeDisplay(value);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['reporting-fx-rates'] });

  const refresh = useMutation({
    mutationFn: refreshFxRates,
    onSuccess: invalidate,
  });
  const save = useMutation({
    mutationFn: (rates: Record<string, number>) => saveFxRates({ rates }),
    onSuccess: () => {
      setEditing(false);
      invalidate();
    },
  });

  const startEditing = (): void => {
    setDraft(
      Object.fromEntries(
        Object.entries(snapshot?.rates ?? {}).map(([code, rate]) => [code, String(rate)]),
      ),
    );
    setEditing(true);
  };

  const commitEdit = (): void => {
    const rates: Record<string, number> = {};
    for (const [code, raw] of Object.entries(draft)) {
      const n = Number(raw);
      // A blank or unreadable box is skipped rather than sent as 0 — the server
      // would reject a 0 anyway, and silently zeroing a rate would make every
      // figure in that currency wrong.
      if (Number.isFinite(n) && n > 0) rates[code] = n;
    }
    save.mutate(rates);
  };

  return (
    <div className="relative" ref={ref}>
      <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
        <span className="text-xs text-muted-foreground">Currency</span>
        <span className="ml-1.5 font-medium">{native ? 'Native' : value}</span>
        <ChevronDown className="ml-1 h-3.5 w-3.5" />
      </Button>
      {open && (
        <div
          role="menu"
          className="scroll-subtle absolute right-0 z-30 mt-1 max-h-[28rem] w-72 overflow-y-auto rounded-md border border-border bg-popover p-1 text-sm shadow-lg"
        >
          <Option
            label="Native"
            hint="Each currency shown separately — never summed"
            checked={native}
            onClick={() => {
              onChange(NATIVE_DISPLAY_CURRENCY);
              setOpen(false);
            }}
          />
          {codes.length > 0 && (
            <div className="mt-1 border-t border-border px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground">
              Convert everything to
            </div>
          )}
          {codes.map((code) => (
            <Option
              key={code}
              label={code}
              checked={!native && value === code}
              onClick={() => {
                onChange(code);
                setOpen(false);
              }}
            />
          ))}

          {codes.length === 0 && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              No exchange rates stored yet
              {isAdmin ? ' — refresh below to pull this month’s.' : '. An admin can pull them.'}
            </p>
          )}

          <div className="mt-1 border-t border-border px-2 pb-1 pt-2 text-xs text-muted-foreground">
            {snapshot ? (
              <>
                {snapshot.month} rates · base {snapshot.base} ·{' '}
                {snapshot.source === 'manual' ? 'hand-edited' : 'ECB via frankfurter'}
                {/* ⚠️ A failed refresh leaves the OLD rates in place, so the
                    error has to be visible here or the staleness is silent. */}
                {snapshot.lastError && (
                  <span className="mt-1 block text-amber-600">
                    Last refresh failed ({snapshot.lastError}) — still using these rates.
                  </span>
                )}
              </>
            ) : (
              'No snapshot stored'
            )}
          </div>

          {isAdmin && (
            <div className="mt-1 border-t border-border px-1 pt-1">
              {!editing ? (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => refresh.mutate()}
                    disabled={refresh.isPending}
                    className="inline-flex flex-1 items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm hover:bg-accent disabled:opacity-50"
                  >
                    <RefreshCw
                      className={cn('h-3.5 w-3.5', refresh.isPending && 'animate-spin')}
                      aria-hidden
                    />
                    Refresh rates
                  </button>
                  {snapshot && (
                    <button
                      type="button"
                      onClick={startEditing}
                      className="rounded px-2 py-1.5 text-sm hover:bg-accent"
                    >
                      Edit
                    </button>
                  )}
                </div>
              ) : (
                <div className="px-1 pb-1">
                  <p className="px-1 pb-1 text-xs text-muted-foreground">
                    Value of 1 {snapshot?.base} in each currency.
                  </p>
                  {Object.keys(draft)
                    .sort()
                    .map((code) => (
                      <label key={code} className="flex items-center gap-2 px-1 py-0.5 text-sm">
                        <span className="w-12 shrink-0 text-muted-foreground">{code}</span>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          value={draft[code] ?? ''}
                          // The base is 1 against itself by definition; the
                          // server imposes it anyway, so it is not editable.
                          disabled={code === snapshot?.base}
                          onChange={(e) =>
                            setDraft((d) => ({ ...d, [code]: e.target.value }))
                          }
                          className="h-7 w-full rounded border border-input bg-background px-1.5 text-right text-sm disabled:opacity-50"
                        />
                      </label>
                    ))}
                  <div className="mt-1 flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={commitEdit} disabled={save.isPending}>
                      Save rates
                    </Button>
                  </div>
                </div>
              )}
              {refresh.data && !refresh.data.ok && (
                <p className="px-2 pb-1 text-xs text-amber-600">
                  Refresh failed ({refresh.data.error}) — the stored rates are unchanged.
                </p>
              )}
              {save.isError && (
                <p className="px-2 pb-1 text-xs text-destructive">
                  {(save.error as Error).message}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Option({
  label,
  hint,
  checked,
  onClick,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      onClick={onClick}
      className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
    >
      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
        {checked && <Check className="h-3.5 w-3.5" />}
      </span>
      <span className="min-w-0">
        <span className="block">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </button>
  );
}
